import { useCallback, useMemo, useRef } from 'react';

import { AreaPickerServiceFactory } from '../services/AreaPicker/AreaPickerServiceFactory';
import { AreaPickerStartOptions } from '../services/AreaPicker/IAreaPickerService';

/**
 * Hook around `IAreaPickerService` that hides the token-functionality and
 * automatically cancels this caller's pending pick on unmount.
 */
export function useAreaPicker() {
  const svc = useMemo(() => AreaPickerServiceFactory.getInstance().createAreaPickerService(), []);

  const tokenRef = useRef<symbol | null>(null);

  const cancel = useCallback(() => {
    if (tokenRef.current === null) return;

    const token = tokenRef.current;
    tokenRef.current = null;
    svc.cancel(token);
  }, [svc]);

  const start = useCallback(
    (options: AreaPickerStartOptions) => {
      if (tokenRef.current !== null) {
        const token = tokenRef.current;
        tokenRef.current = null;
        svc.cancel(token);
      }

      tokenRef.current = svc.start({
        ...options,
        onCancel: () => {
          tokenRef.current = null;
          options.onCancel?.();
        },
      });
    },
    [svc],
  );

  const refresh = useCallback(
    (enabledAreaIds: string[]) => {
      if (tokenRef.current === null) return;

      svc.refresh(enabledAreaIds);
    },
    [svc],
  );

  return useMemo(
    () => ({
      start,
      refresh,
      cancel,
    }),
    [cancel, refresh, start],
  );
}
